import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { DialogService, DynamicDialogConfig } from '@openng/optimus-ui/dynamicdialog';
import { Auth, User, reload, sendEmailVerification } from '@angular/fire/auth';
import { EMPTY, first, from, map, Observable } from 'rxjs';

import { EmailVerificationDialogComponent } from '../components/email-verification-dialog/email-verification-dialog.component';

export interface EmailVerificationDialogData {
  titleKey: string;
  messageKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailVerificationService {
  private dialogService: DialogService = inject(DialogService);
  private auth: Auth = inject(Auth);
  private injector: Injector = inject(Injector);

  private readonly SESSION_KEY: string = 'email_verification_prompted';
  dialogStyles: DynamicDialogConfig = {
    width: '385px',
    maskStyleClass: 'blur-backdrop',
    modal: true,
    closable: true,
    styleClass: 'twofa-dialog',
  };

  /**
   * Abre el aviso de verificación de correo una sola vez por sesión,
   * solo si el usuario aún no ha verificado su correo.
   */
  promptIfNotVerified(): void {
    if (sessionStorage.getItem(this.SESSION_KEY)) return;
    this.checkEmailVerified()
      .pipe(first())
      .subscribe((verified: boolean) => {
        if (verified) return;
        sessionStorage.setItem(this.SESSION_KEY, 'true');
        this.openVerificationDialog({
          titleKey: 'emailVerification.reminder.title',
          messageKey: 'emailVerification.reminder.message',
        });
      });
  }

  /**
   * Permite que el aviso vuelva a mostrarse en el próximo inicio de sesión.
   */
  clearPrompt(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Abre el diálogo de verificación de correo.
   *
   * @param data Claves de traducción del título y mensaje a mostrar.
   */
  openVerificationDialog(data: EmailVerificationDialogData): void {
    this.dialogService.open(EmailVerificationDialogComponent, {
      ...this.dialogStyles,
      data,
    });
  }

  /**
   * Envía al usuario actual el correo de verificación de Firebase.
   *
   * @returns Observable que completa cuando el correo fue enviado.
   */
  sendVerificationEmail(): Observable<void> {
    const user: User | null = this.auth.currentUser;
    if (!user) return EMPTY;
    return from(
      runInInjectionContext(this.injector, () => sendEmailVerification(user))
    );
  }

  /**
   * Refresca el usuario desde Firebase y devuelve si ya verificó su correo.
   */
  checkEmailVerified(): Observable<boolean> {
    const user: User | null = this.auth.currentUser;
    if (!user) return EMPTY;
    return from(runInInjectionContext(this.injector, () => reload(user))).pipe(
      map(() => user.emailVerified)
    );
  }
}
